import { Drawer } from 'src/components/widgets/drawer';

import styled from 'styled-components';

// ========== Layout ==========

export const SidePanelDrawer = styled(Drawer)`
    & > div:first-child {
        border-bottom: none;
    }
` as typeof Drawer;

// ========== Header ==========

export const PanelHeaderContent = styled.div`
    display: flex;
    align-items: center;
    padding: 14px 24px;
    background: #fafbfc;
    gap: 12px;
    flex: 1;
    min-width: 0;
`;

export const PanelHeaderImg = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    overflow: hidden;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

export const PanelHeaderInfo = styled.div`
    flex: 1;
    min-width: 0;

    h2 {
        font-size: 16px;
        font-weight: 700;
        color: #111827;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const HeaderMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 13px;
    color: #6b7280;
    flex-wrap: wrap;
`;

export const HeaderSep = styled.span`
    color: #d1d5db;
`;

export const HeaderPreviewButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 32px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid #dbeafe;
    background: #eff6ff;
    font-size: 12px;
    font-weight: 600;
    color: #2563eb;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
    transition: all 0.15s;

    &:hover {
        background: #dbeafe;
        border-color: #bfdbfe;
    }
`;

// ========== Status Bar ==========

export const StatusBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    background: white;
    flex-shrink: 0;
    flex-wrap: wrap;
`;

export const ToolBadge = styled.span<{ $type: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    background: ${(p) => (p.$type === 'CUSTOMIZER' ? '#ede9fe' : p.$type === 'COMPOSER' ? '#fce7f3' : '#e0f2fe')};
    color: ${(p) => (p.$type === 'CUSTOMIZER' ? '#7c3aed' : p.$type === 'COMPOSER' ? '#db2777' : '#0284c7')};
`;

export const StatusBadge = styled.span<{ $online: boolean; $clickable?: boolean }>`
    display: inline-flex;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    background: ${(p) => (p.$online ? '#d6f3e2' : '#f3f4f6')};
    color: ${(p) => (p.$online ? '#065f46' : '#374151')};
    ${(p) =>
        p.$clickable &&
        `
		cursor: pointer;
		transition: opacity 0.15s;
		&:hover { opacity: 0.75; }
	`}
`;

export const StatusBarSpacer = styled.div`
    flex: 1;
`;

export const ChannelsBadge = styled.span<{ $count: number }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    background: ${(p) => (p.$count > 0 ? '#dbeafe' : '#f3f4f6')};
    color: ${(p) => (p.$count > 0 ? '#1e40af' : '#9ca3af')};
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
        background: ${(p) => (p.$count > 0 ? '#bfdbfe' : '#e5e7eb')};
    }
`;

export const ChannelsDropdown = styled.div`
    padding: 6px 24px 12px;
    border-bottom: 1px solid #f3f4f6;
    background: #fafbfc;
`;

export const ChannelItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 10px;
    border-radius: 6px;
    font-size: 13px;
    color: #374151;
    transition: background 0.15s;

    &:hover {
        background: #f3f4f6;
    }
`;

export const ChannelDot = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #3b82f6;
    flex-shrink: 0;
`;

export const ChannelMeta = styled.span`
    font-size: 11px;
    color: #9ca3af;
`;

export const ChannelDisconnectButton = styled.button`
    margin-left: auto;
    font-size: 11px;
    font-weight: 600;
    color: #dc2626;
    background: none;
    border: none;
    padding: 0 4px;
    cursor: pointer;
    font-family: inherit;
    opacity: 0;
    transition: opacity 0.15s;

    ${ChannelItem}:hover & {
        opacity: 1;
    }

    &:hover {
        text-decoration: underline;
    }
`;

// ========== Body ==========

export const SidePanelScrollArea = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
`;

export const PanelBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0;

    & > :nth-child(2) {
        border-top: none;
    }
`;

export const PanelFooter = styled.div`
    padding: 12px 24px;
    background: #fafbfc;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const FooterButton = styled.button<{ $primary?: boolean; $danger?: boolean; $publish?: boolean; $unpublish?: boolean }>`
    flex: 1;
    padding: 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    text-align: center;
    font-family: inherit;
    border: ${(p) =>
        p.$primary ? 'none' : p.$danger ? '1px solid #fecaca' : p.$publish ? '1px solid #a7f3d0' : p.$unpublish ? '1px solid #c7d2fe' : '1px solid #d1d5db'};
    background: ${(p) => (p.$primary ? '#F46200' : p.$publish ? '#d6f3e2' : p.$unpublish ? '#dde6fe' : 'white')};
    color: ${(p) => (p.$primary ? 'white' : p.$danger ? '#dc2626' : p.$publish ? '#065f46' : p.$unpublish ? '#3730a3' : '#374151')};
    transition: all 0.15s;

    &:hover:not(:disabled) {
        background: ${(p) => (p.$primary ? '#e05a00' : p.$danger ? '#fef2f2' : p.$publish ? '#bbf7d0' : p.$unpublish ? '#c7d2fe' : '#f3f4f6')};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

export const DeleteOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
`;

export const DeleteCard = styled.div`
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    padding: 28px 24px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    max-width: 380px;
    width: 100%;
`;

export const DeleteConfirmText = styled.p`
    font-size: 15px;
    color: #374151;
    text-align: center;
    line-height: 1.5;
    margin: 0;

    strong {
        color: #111827;
    }
`;

export const DeleteConfirmActions = styled.div`
    display: flex;
    gap: 8px;
    width: 100%;
`;

// ========== Group Divider ==========

export const GroupLabel = styled.div`
    padding: 10px 24px 4px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #9ca3af;
    background: #fafbfc;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #f3f4f6;
`;

export const FilterBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 6px 24px;
    background: white;
    font-size: 11px;

    label {
        font-size: 11px;
    }
`;

export const FilterBarLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin-right: auto;
    font-size: 11px;
    color: #6b7280;
    cursor: pointer;
    text-decoration: none;

    &:hover {
        color: #2563eb;
    }
`;

// ========== Sections ==========

export const SectionContainer = styled.div`
    border-bottom: 1px solid #f3f4f6;

    &:last-child {
        border-bottom: none;
    }
`;

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 24px;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;

    &:hover {
        background: #f9fafb;
    }
`;

export const SectionLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const SectionArrow = styled.span<{ $open: boolean }>`
    font-size: 12px;
    color: #9ca3af;
    transition: transform 0.2s;
    display: inline-block;
    transform: rotate(${(p) => (p.$open ? '90deg' : '0deg')});
`;

export const SectionTitle = styled.span`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6b7280;
`;

export const LearnMoreLink = styled.a`
    font-size: 11px;
    color: #6b7280;
    text-decoration: none;

    &:hover {
        color: #2563eb;
        text-decoration: underline;
    }
`;

export const FooterGuideLink = styled.a`
    display: block;
    font-size: 11px;
    color: #6b7280;
    text-decoration: none;
    padding: 6px 0 0;
    text-align: right;

    &:hover {
        color: #2563eb;
        text-decoration: underline;
    }
`;

export const SectionCount = styled.span<{ $isZero?: boolean }>`
    font-size: 11px;
    font-weight: 600;
    padding: 1px 8px;
    border-radius: 10px;
    background: ${(p) => (p.$isZero ? '#f3f4f6' : '#dbeafe')};
    color: ${(p) => (p.$isZero ? '#6b7280' : '#1e40af')};
`;

export const SectionBody = styled.div<{ $collapsed: boolean }>`
    display: ${(p) => (p.$collapsed ? 'none' : 'block')};
    padding: 0 24px 14px;
`;

export const ItemsList = styled.div<{ $tall?: boolean }>`
    max-height: ${(p) => (p.$tall ? '300px' : '200px')};
    overflow-y: auto;
    border-radius: 8px;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 4px;
    }
`;

export const PanelItem = styled.div<{ $clickable?: boolean; $dimmed?: boolean; $disabled?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 10px;
    border-radius: 6px;
    font-size: 13px;
    color: #374151;
    transition: all 0.15s;
    cursor: ${(p) => (p.$disabled ? 'not-allowed' : p.$clickable ? 'pointer' : 'default')};
    border: 1px solid transparent;
    ${(p) => p.$dimmed && 'opacity: 0.4; pointer-events: none;'}
    ${(p) => p.$disabled && 'opacity: 0.45; --panel-item-action-hover-opacity: 0;'}

    &:hover {
        background: ${(p) => (p.$disabled ? 'transparent' : '#eff6ff')};
        border-color: ${(p) => (p.$disabled ? 'transparent' : '#bfdbfe')};
    }
`;

export const ItemDot = styled.div<{ $color: string }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${(p) => p.$color};
`;

export const ItemName = styled.span`
    flex: 1;
    gap: 4px;
    display: flex;
`;

export const ItemMeta = styled.span`
    font-size: 12px;
    color: #9ca3af;
`;

export const ItemAction = styled.span`
    font-size: 11px;
    color: #2563eb;
    opacity: 0;
    transition: opacity 0.15s;
    font-weight: 600;
    white-space: nowrap;

    ${PanelItem}:hover & {
        opacity: var(--panel-item-action-hover-opacity, 1);
    }
`;

export const ColorSwatch = styled.div<{ $color: string }>`
    width: 20px;
    height: 20px;
    border-radius: 4px;
    flex-shrink: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: ${(p) => p.$color};
`;

export const ItemPreviewImg = styled.img`
    width: 28px;
    height: 28px;
    border-radius: 4px;
    object-fit: contain;
    flex-shrink: 0;
    border: 1px solid #e5e7eb;
    background: white;
`;

export const EmptyState = styled.div`
    padding: 12px 16px;
    font-size: 13px;
    color: #9ca3af;
    font-style: italic;
    text-align: center;
    background: #f9fafb;
    border-radius: 8px;
`;

export const StructureTransitionWrapper = styled.div`
    position: relative;
    overflow: hidden;
`;

export const StructureTransitionPanel = styled.div<{ $visible: boolean }>`
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
    opacity: ${(p) => (p.$visible ? 1 : 0)};
    transform: translateY(${(p) => (p.$visible ? '0' : '-8px')});
    ${(p) => !p.$visible && 'position: absolute; top: 0; left: 0; right: 0; pointer-events: none;'}
`;

export const StructureSummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 10px 0;
`;

export const StructureSummaryCard = styled.div`
    background: #f9fafb;
    border: 1px solid #f3f4f6;
    border-radius: 8px;
    padding: 8px 10px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

export const StructureSummaryValue = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
`;

export const StructureSummaryLabel = styled.div`
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
`;

export const StructureSection = styled.div`
    padding: 10px 24px;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;

    ${SectionContainer} {
        margin-left: -24px;
        margin-right: -24px;
    }
`;

export const SectionTabBar = styled.div`
    display: flex;
    gap: 0;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 10px;
`;

export const SectionTab = styled.button<{ $active: boolean }>`
    flex: 1;
    padding: 7px 4px;
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    color: ${(p) => (p.$active ? '#2563eb' : '#9ca3af')};
    border-bottom: 2px solid ${(p) => (p.$active ? '#2563eb' : 'transparent')};
    transition: all 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    white-space: nowrap;

    &:hover {
        color: ${(p) => (p.$active ? '#2563eb' : '#6b7280')};
    }
`;

export const SectionTabCount = styled.span<{ $active: boolean }>`
    font-size: 10px;
    font-weight: 700;
    padding: 0 5px;
    border-radius: 8px;
    background: ${(p) => (p.$active ? '#dbeafe' : '#f3f4f6')};
    color: ${(p) => (p.$active ? '#1e40af' : '#9ca3af')};
`;

export const StructureSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

export const StructureSectionTitle = styled.span`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #9ca3af;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const InfoIcon = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1.5px solid #d1d5db;
    font-size: 9px;
    font-weight: 700;
    font-style: italic;
    color: #9ca3af;
    text-decoration: none;
    text-transform: none;
    letter-spacing: 0;
    line-height: 1;
    flex-shrink: 0;
    transition: all 0.15s;

    &:hover {
        border-color: #9ca3af;
        color: #6b7280;
        background: #f3f4f6;
    }
`;

export const StructureTreeToggle = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: white;
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;

    &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
        color: #374151;
    }
`;

export const StructureTree = styled.div`
    margin-top: 8px;
    border: 1px solid #f3f4f6;
    border-radius: 8px 8px 0 0;
    overflow: hidden;
    max-height: 300px;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 4px;
    }
`;

export const TreeVariant = styled.div`
    &:not(:last-child) {
        border-bottom: 1px solid #f3f4f6;
    }
`;

export const TreeVariantHeader = styled.div<{ $clickable?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    background: #fafbfc;
    cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
    user-select: none;

    &:hover {
        background: ${(p) => (p.$clickable ? '#f3f4f6' : '#fafbfc')};
    }
`;

export const TreeSide = styled.div<{ $clickable?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px 6px 28px;
    font-size: 12px;
    color: #6b7280;
    border-top: 1px solid #f9fafb;
    cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};

    &:hover {
        background: ${(p) => (p.$clickable ? '#f3f4f6' : 'transparent')};
    }
`;

export const TreePrintArea = styled.div<{ $clickable?: boolean }>`
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 4px 12px 4px 48px;
    font-size: 11px;
    color: #9ca3af;
    border-top: 1px solid #fafbfc;
    ${(props) =>
        props.$clickable &&
        `
        cursor: pointer;
        &:hover {
            background: #f9fafb;
            color: #6b7280;
        }
    `}
`;

export const TreePrintAreaDim = styled.span`
    font-size: 10px;
    color: #b0b7c3;
    margin-left: 2px;
    &::before {
        content: '·';
        margin-right: 4px;
    }
`;

export const TreeIcon = styled.span`
    font-size: 10px;
    color: #d1d5db;
    flex-shrink: 0;
    width: 14px;
    text-align: center;
`;

export const ConfiguratorAttrList = styled.div`
    margin-top: 8px;
    border-top: 1px solid #f3f4f6;
    padding-top: 6px;
`;

export const ConfiguratorAttrItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 12px;
    color: #374151;
`;

export const ConfiguratorAttrMeta = styled.span`
    font-size: 11px;
    color: #9ca3af;
`;

export const TreeManageLink = styled.div`
    padding: 8px 12px;
    text-align: center;
    border: 1px solid #f3f4f6;
    border-top: none;
    border-radius: 0 0 8px 8px;
    background: #fafbfc;

    a,
    span {
        font-size: 12px;
        color: #2563eb;
        font-weight: 500;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
`;

// ========== Stacked Panel (L2) ==========

export const StackedBreadcrumb = styled.div`
    font-size: 12px;
    color: #9ca3af;
    padding: 8px 24px;
    border-bottom: 1px solid #f3f4f6;
    background: white;

    span {
        color: #2563eb;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
`;

export const StackedBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 24px;
`;

export const StackedFooterButtons = styled.div`
    display: flex;
    gap: 8px;
    padding: 16px 24px;
`;

export const FullModeLink = styled.button`
    display: block;
    width: 100%;
    font-size: 13px;
    color: #f46200;
    font-weight: 500;
    text-decoration: none;
    padding: 10px 24px;
    border: none;
    border-bottom: 1px solid #e5e7eb;
    background: transparent;
    cursor: pointer;
    font-family: inherit;
    text-align: center;

    &:hover {
        text-decoration: underline;
    }
`;

// ========== Form Elements (L2) ==========

export const FormGroup = styled.div`
    margin-bottom: 20px;
`;

export const FormLabel = styled.label`
    display: block;
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
    margin-bottom: 4px;
`;

export const FormInput = styled.input<{ $isError?: boolean }>`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${(p) => (p.$isError ? '#dc2626' : '#d1d5db')};
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.15s;

    &:focus {
        outline: none;
        border-color: ${(p) => (p.$isError ? '#dc2626' : '#2563eb')};
        box-shadow: 0 0 0 3px ${(p) => (p.$isError ? 'rgba(220, 38, 38, 0.12)' : 'rgba(37, 99, 235, 0.1)')};
    }
`;

export const FormSelect = styled.select`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    background: white;
`;

export const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

export const FormSwitchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #f3f4f6;
`;

export const FormSwitchLabel = styled.span`
    font-size: 14px;
    color: #374151;
`;

export const FormHelp = styled.p`
    font-size: 12px;
    color: #9ca3af;
    margin-top: 4px;
    margin-bottom: 0;
`;

export const FormSectionTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #9ca3af;
    margin-top: 24px;
    margin-bottom: 10px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;

    &:first-child {
        margin-top: 0;
        padding-top: 0;
        border-top: none;
    }
`;

export const SettingHint = styled.span`
    display: block;
    font-size: 11px;
    color: #9ca3af;
    margin-top: 2px;
    font-style: italic;
`;

export const SettingHintLink = styled.button`
    font-size: 11px;
    color: #f46200;
    font-weight: 500;
    font-style: italic;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

export const CheckboxGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 16px;
    margin-top: 4px;
`;

export const CompactFieldLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;
`;

export const CompactSelect = styled.select`
    width: 100%;
    padding: 6px 8px;
    font-size: 13px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    color: #374151;
`;

export const CompactTextarea = styled.textarea`
    width: 100%;
    padding: 8px;
    font-size: 13px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    color: #374151;
    box-sizing: border-box;
`;

export const FullModeHint = styled.div`
    font-size: 12px;
    color: #9ca3af;
    text-align: center;
    padding: 0 24px 4px;
`;

export const FullModeIframeContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

export const FullModeSkeleton = styled.div`
    position: absolute;
    inset: 0;
    padding: 32px 40px;
    background: #fff;
    z-index: 1;
`;

export const SectionCard = styled.div`
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 10px;

    /* Match checkbox label size to switch labels */
    input[type='checkbox'] + div label,
    input[type='checkbox'] ~ div label {
        font-size: 12px;
        line-height: 16px;
        color: #374151;
        font-weight: 400;
    }
`;

export const SharedWarningCallout = styled.div`
    background: #fffbeb;
    border: 1px solid #f59e0b;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 10px;
    font-size: 12px;
    line-height: 1.5;
    color: #92400e;
`;

export const WarningCalloutLink = styled.button`
    font-size: 12px;
    color: #92400e;
    font-weight: 600;
    text-decoration: underline;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    &:hover {
        color: #78350f;
    }
`;

export const SectionCardTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const CompactSwitchGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 2px;
`;

export const CompactSwitchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 12px;
    color: #374151;
`;

export const CompactSwitchLabel = styled.span`
    font-size: 12px;
    color: #374151;
`;

// ========== Fullscreen Overview ==========

export const OverviewHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 32px;
    background: #fafbfc;
    flex-shrink: 0;
`;

export const OverviewHeaderInfo = styled.div`
    flex: 1;
    min-width: 0;

    h2 {
        font-size: 18px;
        font-weight: 700;
        color: #111827;
        margin: 0;
    }
`;

export const OverviewHeaderMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    font-size: 13px;
    color: #6b7280;
`;

export const OverviewBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 24px 32px;
`;

export const OverviewGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 16px;
`;

export const OverviewCategoryTitle = styled.div`
    grid-column: 1 / -1;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #9ca3af;
    padding: 8px 0 0;
    margin-top: 8px;
    border-top: 1px solid #e5e7eb;

    &:first-child {
        margin-top: 0;
        border-top: none;
    }
`;

export const OverviewCard = styled.div`
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 16px 20px;
`;

export const OverviewCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
`;

export const OverviewCardTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #374151;
`;

export const OverviewCardBadge = styled.span<{ $active: boolean }>`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 10px;
    background: ${(p) => (p.$active ? '#dbeafe' : '#f3f4f6')};
    color: ${(p) => (p.$active ? '#1e40af' : '#9ca3af')};
`;

export const OverviewCardItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 0;
    font-size: 13px;
    color: #374151;
`;

export const OverviewCardEmpty = styled.div`
    font-size: 13px;
    color: #9ca3af;
    font-style: italic;
`;

// ========== Pricing Rule Editor ==========

export const PricingRuleCard = styled.div`
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 8px;
    overflow: hidden;
`;

export const PricingRuleCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: #f3f4f6;
    cursor: pointer;
    border-left: 3px solid #d1d5db;
    transition: border-color 0.15s;

    &:hover {
        background: #e5e7eb;
        border-left-color: #f46200;
    }
`;

export const PricingRuleCardBody = styled.div`
    padding: 8px 12px;
    border-top: 1px solid #f3f4f6;
`;

export const ConditionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 12px;
    color: #374151;
`;

export const PriceInput = styled.input`
    width: 80px;
    padding: 3px 6px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 12px;
    text-align: right;

    &:focus {
        outline: none;
        border-color: #3b82f6;
    }
`;

export const DeleteRuleButton = styled.button`
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    font-size: 14px;
    padding: 2px 6px;
    border-radius: 4px;
    flex-shrink: 0;

    &:hover {
        background: #fef2f2;
    }
`;

export const QuantityTierContainer = styled.div`
    border-left: 3px solid #f46200;
    padding: 8px 8px 8px 10px;
    margin-top: 4px;
`;

export const QuantityTierRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 6px;
    padding: 3px 0;
    font-size: 12px;

    > div {
        flex: 0 0 80px;
        min-width: 0;
    }
`;

// ========== Composer Attribute Editor ==========

export const OptionExpandedCard = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin: 6px 0;
    overflow: hidden;
`;

export const OptionExpandedHeader = styled.div<{ $expanded: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    background: ${(p) => (p.$expanded ? '#f9fafb' : 'white')};
    transition: background 0.15s;

    &:hover {
        background: #f3f4f6;
    }
`;

export const OptionExpandedBody = styled.div`
    padding: 12px;
    border-top: 1px solid #e5e7eb;
    background: #fafbfc;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const OptionPreviewImg = styled.img`
    width: 32px;
    height: 32px;
    border-radius: 4px;
    object-fit: contain;
    border: 1px solid #e5e7eb;
    flex-shrink: 0;
`;

export const OptionFieldRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

export const OptionFieldLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #9ca3af;
`;

export const OptionFieldValue = styled.span`
    font-size: 13px;
    color: #374151;
`;

export const ActionCard = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: white;
    border: 1px solid #f3f4f6;
    border-radius: 6px;
    font-size: 12px;
    color: #374151;
`;

export const ActionTypeBadge = styled.span`
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 4px;
    background: #ede9fe;
    color: #7c3aed;
    white-space: nowrap;
`;

export const ActionColorSwatch = styled.div<{ $color: string }>`
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: ${(p) => p.$color || '#000'};
    border: 1px solid #d1d5db;
    cursor: pointer;
    flex-shrink: 0;
    transition: box-shadow 0.15s;

    &:hover {
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    }
`;

export const ColorPickerPopover = styled.div`
    position: fixed;
    z-index: 9999;
    background: white;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    padding: 12px;
`;

export const ColorPickerCover = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 9998;
`;

export const InlineInput = styled.input`
    width: 100%;
    padding: 6px 10px;
    border: 1px solid transparent;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    background: transparent;
    transition:
        border-color 0.15s,
        background 0.15s;

    &:hover {
        background: #f3f4f6;
    }

    &:focus {
        outline: none;
        border-color: #2563eb;
        background: white;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
`;

export const InlineSelect = styled.select`
    width: 100%;
    padding: 6px 10px;
    border: 1px solid transparent;
    border-radius: 6px;
    font-size: 12px;
    font-family: inherit;
    background: transparent;
    transition:
        border-color 0.15s,
        background 0.15s;

    &:hover {
        background: #f3f4f6;
    }

    &:focus {
        outline: none;
        border-color: #2563eb;
        background: white;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
`;

export const AttributeCard = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 8px;
    overflow: hidden;
`;

export const AttributeCardHeader = styled.div<{ $expanded: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    background: ${(p) => (p.$expanded ? '#f0f4ff' : 'white')};
    border-bottom: ${(p) => (p.$expanded ? '1px solid #e5e7eb' : 'none')};
    transition: background 0.15s;
    font-weight: 600;
    font-size: 13px;
    color: #1e293b;

    &:hover {
        background: #f0f4ff;
    }
`;

export const AttributeCardBody = styled.div`
    background: #fafbfc;
`;

// ========== Add Panel ==========

export const ChoiceCard = styled.div`
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        border-color: #2563eb;
        background: #eff6ff;
    }
`;

export const ChoiceCardTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
`;

export const ChoiceCardDesc = styled.div`
    font-size: 13px;
    color: #6b7280;
`;

export const PrintMethodPickList = styled.div`
    max-height: 340px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 4px;
    }
`;

export const PrintMethodPickItem = styled.div<{ $alreadyAdded?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid #f3f4f6;
    cursor: ${(p) => (p.$alreadyAdded ? 'default' : 'pointer')};
    opacity: ${(p) => (p.$alreadyAdded ? 0.5 : 1)};
    transition: background 0.15s;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${(p) => (p.$alreadyAdded ? 'transparent' : '#f9fafb')};
    }
`;

export const PrintMethodPickName = styled.span`
    font-size: 14px;
    color: #374151;
`;

export const PrintMethodPickTag = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
`;

export const LoadingText = styled.div`
    padding: 20px;
    text-align: center;
    font-size: 13px;
    color: #9ca3af;
`;

// ========== Variant Extensions ==========

export const ItemActionDanger = styled(ItemAction)`
    color: #dc2626;
`;

export const ItemActionSuccess = styled(ItemAction)`
    color: #16a34a;
`;

export const InlineAction = styled.span<{ $danger?: boolean }>`
    font-size: 11px;
    font-weight: 600;
    color: ${(p) => (p.$danger ? '#dc2626' : '#2563eb')};
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;

export const ItemNameAction = styled(ItemName)`
    color: #2563eb;
    font-weight: 500;
`;

export const PanelItemChild = styled(PanelItem)`
    padding-left: 28px;
`;

export const PanelItemSpaced = styled(PanelItem)`
    margin-top: 4px;
`;

export const PanelItemSpacedLarge = styled(PanelItem)`
    margin-top: 6px;
`;

export const PanelItemDimmed = styled(PanelItem)<{ $dimmed?: boolean }>`
    ${(props) =>
        props.$dimmed &&
        `
		opacity: 0.4;
		pointer-events: none;
	`}
`;

export const TreeSideFlush = styled(TreeSide)`
    padding-left: 0;
`;

export const ItemMetaPushed = styled(ItemMeta)`
    margin-left: auto;
`;

export const ImagePlaceholder = styled.span`
    font-size: 13px;
    color: #9ca3af;
`;

export const ConditionalBadge = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: #b45309;
    background: #fef3c7;
    padding: 0 5px;
    border-radius: 3px;
`;

export const PriceBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    padding: 1px 6px;
    border-radius: 4px;
`;

export const ConditionalBadgeLarge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #b45309;
    background: #fef3c7;
    padding: 1px 6px;
    border-radius: 4px;
`;

export const InfoCallout = styled(SharedWarningCallout)`
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1e40af;
`;

export const SelectAllLink = styled.span`
    font-size: 11px;
    color: #2563eb;
    cursor: pointer;
`;

// ========== Loading Skeletons ==========

export const InlineSkeleton = styled.div`
    padding: 8px 0;
    color: #9ca3af;
    font-size: 12px;
`;

// ========== Form Layout Helpers ==========

export const FormFlexRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const FormFlexStartRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 8px;
`;

export const FormFlexColumn = styled.div`
    flex: 1;
`;

export const FormFixedColumn = styled.div<{ $width: number }>`
    width: ${(p) => p.$width}px;
`;

export const FormGridRow = styled.div<{ $columns?: number }>`
    display: grid;
    grid-template-columns: repeat(${(p) => p.$columns || 2}, 1fr);
    gap: 8px;
    margin-bottom: 12px;
`;

export const CheckboxGridCompact = styled.div<{ $columns?: number }>`
    display: grid;
    grid-template-columns: repeat(${(p) => p.$columns || 2}, 1fr);
    gap: 2px;
`;

export const FormDivider = styled.div`
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #e5e7eb;
`;

export const FormGapRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 4px;
`;

export const FormMarginTop = styled.div`
    margin-top: 8px;
`;

// ========== Pricing Rule Editor ==========

export const ConditionFormRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 6px;
    flex-wrap: nowrap;
`;

export const ConditionLabel = styled.span<{ $minWidth?: number }>`
    font-size: 11px;
    color: #6b7280;
    font-style: italic;
    min-width: ${(p) => p.$minWidth || 10}px;
    text-align: center;
    white-space: nowrap;
    padding-bottom: 6px;
`;

export const ConditionFieldTitle = styled.div`
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
    margin-bottom: 4px;
`;

export const CurrencyLabel = styled.span`
    color: #9ca3af;
    font-size: 10px;
`;

export const ExpandArrow = styled.span`
    font-size: 14px;
    color: #6b7280;
    font-weight: 600;
`;

export const AddTextButton = styled.button`
    background: none;
    border: none;
    color: #3b82f6;
    cursor: pointer;
    font-size: 11px;
    padding: 4px 0 0;
`;

export const AddQuantityButton = styled.button`
    background: none;
    border: none;
    color: #3b82f6;
    cursor: pointer;
    font-size: 10px;
    padding: 0 2px;
    padding-bottom: 4px;
    white-space: nowrap;
    flex-shrink: 0;
`;

export const AddRuleButton = styled.button`
    width: 100%;
    padding: 8px 0;
    margin-top: 8px;
    background: none;
    border: 1px dashed #d1d5db;
    border-radius: 6px;
    color: #3b82f6;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
`;

export const RulePropertyName = styled.div`
    font-weight: 600;
    font-size: 13px;
`;

export const RuleElementTitle = styled.div`
    font-size: 11px;
    color: #6b7280;
`;

export const RuleMetaLine = styled.div`
    font-size: 11px;
    color: #9ca3af;
`;

export const RuleHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const QuantityTierHeader = styled.div`
    display: flex;
    gap: 6px;
    margin-bottom: 4px;
`;

export const QuantityTierLabel = styled.span<{ $width?: number }>`
    font-size: 10px;
    color: #9ca3af;
    ${(p) => (p.$width ? `width: ${p.$width}px;` : 'flex: 1;')}
`;

export const EmptyConditions = styled.div`
    font-size: 11px;
    color: #9ca3af;
    padding: 4px 0;
`;

export const EmptyRulesCenter = styled.div`
    padding: 20px;
    text-align: center;
    color: #9ca3af;
`;

export const EmptyRulesSmall = styled.div`
    padding: 12px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
`;

export const TargetsInfo = styled.div`
    font-size: 11px;
    color: #6b7280;
`;

export const TargetsSection = styled.div`
    margin-top: 8px;
`;

export const FixedWidthSpacer = styled.span`
    width: 22px;
    display: inline-block;
`;

// ========== Composer Attribute Editor ==========

export const ActionEditPanel = styled.div`
    padding: 6px 0 6px 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: #f9fafb;
    border-radius: 6px;
    margin-top: 2px;
    padding-right: 8px;
`;

export const AttributeFieldsSection = styled.div`
    padding: 8px 12px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const OptionDivider = styled.div<{ $showBorder?: boolean }>`
    margin: 0 8px;
    border-bottom: ${(p) => (p.$showBorder ? '1px solid #e5e7eb' : 'none')};
`;

export const ActionCardClickable = styled(ActionCard)`
    cursor: pointer;
`;

export const ActionExpandArrow = styled.span`
    font-size: 10px;
    color: #9ca3af;
    margin-left: auto;
`;

export const ComposerFooter = styled.div`
    padding: 10px 24px;
    text-align: center;
`;

export const RgbInputGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    padding: 0 4px;
`;

export const RgbLabel = styled.span`
    font-size: 10px;
    color: #9ca3af;
    width: 10px;
`;

export const RgbInput = styled.input`
    width: 40px;
    padding: 4px 4px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 12px;
    font-family: inherit;
    text-align: center;
`;

export const HexInputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 0 4px;
`;

export const HexLabel = styled.span`
    font-size: 11px;
    color: #6b7280;
`;

export const HexColorInputStyled = styled.input`
    flex: 1;
    padding: 4px 6px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 12px;
    font-family: inherit;
`;

// ========== Iframe Embed ==========

export const IframeEmbed = styled.iframe<{ $loading?: boolean }>`
    width: 100%;
    height: 100%;
    border: none;
    flex: 1;
    visibility: ${(p) => (p.$loading ? 'hidden' : 'visible')};
    position: ${(p) => (p.$loading ? 'absolute' : 'relative')};
`;

// ========== Skeleton Wrappers ==========

export const SkeletonHeaderWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    padding: 20px 24px;
    gap: 14px;
    background: #fafbfc;
    border-bottom: 1px solid #e5e7eb;
`;

export const SkeletonHeaderContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const SkeletonStatusBarWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border-bottom: 1px solid #f3f4f6;
`;

export const SkeletonSectionWrapper = styled.div`
    padding: 14px 24px;
    border-bottom: 1px solid #f3f4f6;
`;

// ========== Printing Method Editor ==========

export const ColorInputCompact = styled(FormInput)`
    width: 36px;
    height: 24px;
    padding: 1px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
`;

export const FormInputCompact = styled(FormInput)<{ $width?: number }>`
    ${(p) => p.$width && `width: ${p.$width}px;`}
`;

export const FormSelectCompact = styled(FormSelect)`
    flex: 1;
    font-size: 12px;
    padding: 4px 8px;
`;

export const FormLabelCompact = styled(FormLabel)`
    margin-bottom: 4px;
`;

export const FormHelpDanger = styled(FormHelp)`
    color: #dc2626;
`;

export const EffectsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ColorRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
`;

export const TextMarginBottom = styled.div`
    margin-bottom: 10px;
`;
